import React, { useState } from 'react';
import { useSpring, animated, config, useTransition } from 'react-spring';
import { FaChevronDown } from 'react-icons/fa';

const IntroAnimation = ({ onUnveil }) => {
    const [isUnveiling, setIsUnveiling] = useState(false);

    // Spring for the background effect
    const backgroundAnimation = useSpring({
        from: { opacity: 0, transform: 'scale(1.2)' },
        to: { opacity: 1, transform: 'scale(1)' },
        config: { ...config.molasses, duration: 1500 }, // Slower, smoother fade
    });

    // Spring for the title text
    const titleAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(30px) scale(0.9)' },
        to: { opacity: 1, transform: 'translateY(0px) scale(1)' },
        config: { ...config.wobbly, duration: 1000 },
        delay: 500, // Start after background effect begins to appear
    });

    // Spring for the button
    const buttonAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(20px) scale(0.95)' },
        to: { opacity: 1, transform: 'translateY(0px) scale(1)' },
        config: config.gentle,
        delay: 1200, // Start after title
    });

    // Left curtain animation
    const leftCurtainAnimation = useSpring({
        from: { transform: 'translateX(0%)' },
        to: { transform: isUnveiling ? 'translateX(-100%)' : 'translateX(0%)' },
        config: { tension: 180, friction: 24 },
    });

    // Right curtain animation
    const rightCurtainAnimation = useSpring({
        from: { transform: 'translateX(0%)' },
        to: { transform: isUnveiling ? 'translateX(100%)' : 'translateX(0%)' },
        config: { tension: 180, friction: 24 },
    });

    // Handle the unveiling process
    const handleUnveil = () => {
        setIsUnveiling(true);

        // After the curtains are drawn, trigger the actual transition
        setTimeout(() => {
            onUnveil();
        }, 800); // This should match the curtain animation duration
    };

    return (
        // Position this below the navbar (top-16)
        <div className="fixed top-16 inset-x-0 bottom-0 z-30 flex flex-col items-center justify-center overflow-hidden">
            {/* Background that matches the home page */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#f8f9fa] to-[#e9ecef] z-0">
                {/* Light effect containers - matching Home style */}
                <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse-slow"></div>
                <div className="absolute top-1/4 right-0 w-80 h-80 bg-primary/15 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>

                {/* Hints of the actual home content */}
                <div className="absolute inset-0 opacity-10">
                    <div className="container mx-auto h-full flex items-center justify-center">
                        <div className="w-1/2 h-64 rounded-xl bg-white/30 backdrop-blur-sm"></div>
                    </div>
                </div>
            </div>

            {/* Left curtain */}
            <animated.div
                style={leftCurtainAnimation}
                className="absolute left-0 top-0 bottom-0 w-1/2 bg-gradient-to-r from-[#f0f0f0] to-[#ffffff] z-20 shadow-lg border-r border-gray-200/50"
            />

            {/* Right curtain */}
            <animated.div
                style={rightCurtainAnimation}
                className="absolute right-0 top-0 bottom-0 w-1/2 bg-gradient-to-l from-[#f0f0f0] to-[#ffffff] z-20 shadow-lg border-l border-gray-200/50"
            />

            {/* Content layer */}
            <div className="relative z-30 text-center px-4 max-w-4xl">
                <animated.div style={titleAnimation} className="mb-10">
                    <h1 className="text-5xl md:text-7xl font-bold text-[#212529] font-serif tracking-tight">
                        Unveil Our <span className="text-primary font-display italic">Prototype</span>
                    </h1>
                    <div className="h-0.5 w-32 bg-primary mx-auto mt-6"></div>
                    <p className="text-[#212529]/80 text-xl mt-6 max-w-2xl mx-auto font-light">
                        Experience the future of electric motor technology, designed for unparalleled performance
                    </p>
                </animated.div>

                <animated.div style={buttonAnimation} className="flex flex-col items-center">
                    <button
                        onClick={handleUnveil}
                        className="px-12 py-4 text-lg font-medium text-primary rounded-lg shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 mb-4 group"
                        style={{
                            background: 'linear-gradient(145deg, rgba(255,255,255,0.95), rgba(245,245,245,0.9))',
                            border: '1px solid rgba(255,255,255,0.6)',
                            boxShadow: '5px 5px 15px rgba(0,0,0,0.05), -5px -5px 15px rgba(255,255,255,0.5)'
                        }}
                    >
                        Reveal Technology
                    </button>

                    <div className="mt-2 text-[#212529]/60 flex items-center animate-bounce">
                        <FaChevronDown className="h-4 w-4" />
                    </div>
                </animated.div>
            </div>
        </div>
    );
};

export default IntroAnimation; 